import { RfqAutoApproveDraft, RfqAutoRetryDraft, RfqDraft } from '@/features/rfq/types'
import { CreateRfqRequest } from '@/types/generated/public_api'
import { AutoApprove, AutoRetry, RfqHedgeLeg, RfqLeg } from '@/types/generated/rfq'

/**
 * RFQ creation accepts one top-level account even when the ticket has multiple legs.
 * Resolve that account only when every submission row points to the same account;
 * returning undefined lets the caller reject a mixed-account ticket instead of silently
 * sending every leg through an arbitrary account.
 */
export function resolveSingleRfqAccountId(accountIds: string[]): string | undefined {
  const uniqueAccountIds = new Set(accountIds.filter(Boolean))
  return uniqueAccountIds.size === 1 ? uniqueAccountIds.values().next().value : undefined
}

/**
 * Convert a builder draft into the wire `CreateRfqRequest`.
 *
 * No `auto_approve`/`auto_retry` ⇒ a manual RFQ. Auto mode emits exactly the chosen
 * `AutoApprove` threshold oneof and an `AutoRetry` only when enabled. `correlationId`
 * is intentionally left for the caller to stamp (auto starts mirror algo starts).
 */
export function buildCreateRfqRequest(draft: RfqDraft, accountId: string): CreateRfqRequest {
  const legs: RfqLeg[] = draft.legs.map((l) => ({
    symbol: l.symbol.trim(),
    amount: normalizeAmount(l.amount),
    side: l.side,
  }))

  const hedge: RfqHedgeLeg | undefined = draft.hedge.enabled
    ? {
        symbol: draft.hedge.symbol.trim(),
        amount: normalizeAmount(draft.hedge.amount),
        side: draft.hedge.side,
        price: normalizeAmount(draft.hedge.price),
      }
    : undefined

  const makerIds = draft.makerIds.map((m) => m.trim()).filter((m) => m.length > 0)

  const req: CreateRfqRequest = {
    accountId,
    legs,
    hedge,
    makerIds,
    disclosed: draft.disclosed,
  }

  if (draft.executionMode === 'auto') {
    req.autoApprove = buildAutoApprove(draft.autoApprove)
    const retry = buildAutoRetry(draft.autoRetry)
    if (retry) req.autoRetry = retry
  }

  return req
}

/** Strip the order form's grouping commas so the wire amount is a plain decimal string. */
function normalizeAmount(value: string): string {
  return value.replace(/,/g, '').trim()
}

/** Set exactly the chosen `threshold` oneof field. */
function buildAutoApprove(a: RfqAutoApproveDraft): AutoApprove {
  return { [a.kind]: a.value.trim() } as AutoApprove
}

/** Absent fields = retry until filled/cancelled; returns undefined when disabled. */
function buildAutoRetry(r: RfqAutoRetryDraft): AutoRetry | undefined {
  if (!r.enabled) return undefined
  const retry: AutoRetry = {}
  const max = r.maxRetries.trim()
  const until = r.retryUntilMs.trim()
  if (max !== '') retry.maxRetries = Number(max)
  if (until !== '') retry.retryUntilMs = until
  return retry
}
